import React, { forwardRef } from "react";
import { Box, Flex, Input, InputGroup } from "@chakra-ui/react";
import { CalendarIcon } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useCustomTheme } from "../../contexts/CustomThemeContext";

interface CustomDatePickerProps {
  selected: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  label?: string;
  width?: any;

  minDate?: Date;
  maxDate?: Date;
  excludeDates?: Date[];
  filterDate?: (date: Date) => boolean;
}

const CustomInput = forwardRef<HTMLInputElement, any>(
  ({ value, onClick, placeholder, label, width }, ref) => {
    const { theme } = useCustomTheme();

    return (
      <Box position="relative" w="100%">
        {label && (
          <Flex
            color={theme.textMuted}
            alignItems="center"
            top="-20px"
            gap={2}
            fontSize="11px"
            position="absolute"
            display={{ base: "none", md: "flex" }}
          >
            <CalendarIcon size={11} />
            <label style={{ color: theme.textSubtle }}>{label}</label>
          </Flex>
        )}

        <InputGroup onClick={onClick} cursor="pointer" w="100%">
          <Input
            w={width || "100%"}
            ref={ref}
            value={value || ""}
            bg={theme.inputBg}
            color={theme.text}
            border="1px solid"
            borderColor={theme.inputBorder}
            placeholder={placeholder || "Date"}
            readOnly
            _placeholder={{
              fontWeight: "500",
              color: theme.inputPlaceholder,
            }}
            _hover={{ borderColor: theme.inputBorderFocus }}
            _focus={{
              borderColor: theme.inputBorderFocus,
              boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
            }}
            borderRadius="md"
          />
        </InputGroup>
      </Box>
    );
  },
);

CustomInput.displayName = "CustomInput";

export const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  selected,
  onChange,
  placeholder,
  label,
  width,
  minDate,
  maxDate,
  excludeDates,
  filterDate,
}) => {
  return (
    <Box
      w="100%"
      sx={{
        ".custom-datepicker-wrapper": {
          width: "100%",
        },
        ".custom-datepicker-wrapper .react-datepicker__input-container": {
          width: "100%",
        },
      }}
    >
      <DatePicker
        selected={selected}
        onChange={onChange}
        placeholderText={placeholder || "Pick a date"}
        customInput={
          <CustomInput placeholder={placeholder} label={label} width={width} />
        }
        dateFormat="yyyy-MM-dd"
        popperPlacement="bottom-start"
        isClearable
        calendarClassName="custom-datepicker-calendar"
        wrapperClassName="custom-datepicker-wrapper"
        minDate={minDate}
        maxDate={maxDate}
        excludeDates={excludeDates}
        filterDate={filterDate}
      />
    </Box>
  );
};
